import type { SocialPlatform } from "./api/types";

/**
 * The platforms the footer of the website can link to, mirroring
 * src/settings/social-links.ts on the server.
 *
 * Mirrored rather than imported, exactly as client/src/validation.ts mirrors
 * the DTOs: the two are separate deployables in every way but the repository.
 * The server stays the authority - it refuses a link that does not fit - and
 * this is what lets the person filling in the form read what is wrong with it
 * in her own language, on the field it belongs to, before a round trip.
 *
 * A sixth platform is one entry here and one there.
 */
export interface SocialPlatformInfo {
  id: SocialPlatform;
  /** The label on the field. */
  label: string;
  /** The placeholder, and the example in the message when a link does not fit. */
  example: string;
  /** The hosts a link may have, with `www.` and `m.` already taken off. */
  hosts: readonly string[];
}

export const SOCIAL_PLATFORMS: readonly SocialPlatformInfo[] = [
  {
    id: "instagram",
    label: "Instagram",
    example: "https://www.instagram.com/tubetaenzig",
    hosts: ["instagram.com"],
  },
  {
    id: "facebook",
    label: "Facebook",
    example: "https://www.facebook.com/tubetaenzig",
    hosts: ["facebook.com", "fb.com"],
  },
  {
    id: "youtube",
    label: "YouTube",
    example: "https://www.youtube.com/@tubetaenzig",
    hosts: ["youtube.com", "youtu.be", "music.youtube.com"],
  },
  {
    id: "spotify",
    label: "Spotify",
    example: "https://open.spotify.com/artist/…",
    hosts: ["open.spotify.com", "spotify.com", "play.spotify.com"],
  },
  {
    id: "tiktok",
    label: "TikTok",
    example: "https://www.tiktok.com/@tubetaenzig",
    hosts: ["tiktok.com"],
  },
];

/** Mirrors HOST_PREFIX in src/settings/social-links.ts. */
const HOST_PREFIX = /^(?:www|m)\./;

/** Mirrors MAX_SOCIAL_LINK_LENGTH in src/settings/social-links.ts. */
const MAX_LINK_LENGTH = 500;

export function socialPlatform(id: SocialPlatform): SocialPlatformInfo {
  return SOCIAL_PLATFORMS.find(
    (platform) => platform.id === id,
  ) as SocialPlatformInfo;
}

/**
 * `new URL`, and once more with an https:// in front for a link pasted out of
 * an address bar without its scheme. The same two steps the server takes, so
 * that the two cannot disagree about what a host is.
 */
function parse(link: string): URL | null {
  try {
    return new URL(link);
  } catch {
    try {
      return new URL(`https://${link}`);
    } catch {
      return null;
    }
  }
}

/** Whether this is a link to this platform. Empty is not - see validation.ts. */
export function isSocialLink(platform: SocialPlatform, value: string): boolean {
  const link = value.trim();
  if (link === "" || link.length > MAX_LINK_LENGTH) {
    return false;
  }

  const url = parse(link);
  if (url === null) {
    return false;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return false;
  }

  const host = url.hostname.toLowerCase().replace(HOST_PREFIX, "");
  return socialPlatform(platform).hosts.includes(host);
}

/**
 * What she reads when a link does not belong to the field it is in.
 *
 * One sentence that says what is expected and shows it, because "ungültig"
 * would leave her with nothing to do about it.
 */
export function socialLinkError(platform: SocialPlatform): string {
  const { label, example } = socialPlatform(platform);
  return `Bitte einen Link zu ${label} eintragen, zum Beispiel ${example} – oder das Feld leer lassen.`;
}

/**
 * The English the server answers with for the same case.
 *
 * Built the same way socialLinkMessage() builds it in
 * src/settings/social-links.ts, so that api/messages.ts can translate it and
 * the settings page can tell which field it belongs to.
 */
export function socialLinkApiMessage(platform: SocialPlatform): string {
  return `${platform} must be empty or a link to ${socialPlatform(platform).hosts[0]}`;
}
